import React, { useEffect, useRef, useMemo, useState } from "react";
import { Popup } from "devextreme-react/popup";
import { Plus, Download, Search, MoreHorizontal, RefreshCw } from "lucide-react";
import "./SupervisionParticipants.css";
import axios from "axios";
import { getAuthToken, handleGetLocal } from "../../services/auth-service";
import { Toster } from "../../components/Toster/Toster";
import { DateBox } from "devextreme-react";
import ExcelJS from "exceljs";

const columns = [
  { key: "id", label: "Participant Id" },
  { key: "altId", label: "Alt Id" },
  { key: "lastName", label: "Last Name" },
  { key: "firstName", label: "First Name" },
  { key: "agency", label: "Agency" },
  { key: "dob", label: "Birth Date" },
  { key: "frequency", label: "Frequency" },
  { key: "supervisor", label: "Supervisor" },
  { key: "gender", label: "Gender" },
  { key: "lastTestDate", label: "Last Test Date" },
  { key: "nextTestDate", label: "Next Test Date" },
];

const formatName = (person) => `${person.firstName} ${person.lastName}`;

const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const csvCell = (value) => {
  let text = String(value ?? "");
  // Prevent exported values from being interpreted as formulas.
  if (/^[\s]*[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

const getTestDateKey = (value) => {
  if (!value) return "";

  const text = String(value);

  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    return text.substring(0, 10);
  }

  // Handles the randomizer date: 11/02/2026 8:00am - 12:00pm
  const match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);

  return match ? `${match[3]}-${match[1]}-${match[2]}` : "";
};

const formatTestDate = (value) => {
  const dateKey = getTestDateKey(value);

  if (!dateKey) return "—";

  const [year, month, day] = dateKey.split("-");
  return `${month}/${day}/${year}`;
};

export default function SupervisionParticipants() {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({
    key: "id",
    direction: "asc",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [menuId, setMenuId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [popupType, setPopupType] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [participantTests, setParticipantTests] = useState([]);
  const [testsLoading, setTestsLoading] = useState(false);
  const [testsError, setTestsError] = useState("");
  const [availableTestDates, setAvailableTestDates] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState("");
  const [selectedTestDateId, setSelectedTestDateId] = useState("");
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleSaveMessage, setScheduleSaveMessage] = useState("");
  const [addParticipantDropdowns, setAddParticipantDropdowns] = useState({
    agencies: [],
    supervisors: [],
    frequencies: [],
    genders: [],
  });

  const [toastConfig, setToastConfig] = useState({
    isVisible: false,
    type: "",
    message: "",
  });

  const [savingParticipant, setSavingParticipant] = useState(false);
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false);
  const [addParticipantError, setAddParticipantError] = useState("");
  const emptyParticipantForm = {
    firstName: "",
    middleName: "",
    lastName: "",
    altId: "",
    birthDate: "",
    gender: "",
    phoneNumber: "",
    email: "",
    agencyId: "",
    supervisorId: "",
    frequencyId: "",
  };

  const [participantForm, setParticipantForm] = useState({
    ...emptyParticipantForm,
  });

  const handleParticipantFieldChange = (event) => {
    const { name, value } = event.target;

    setParticipantForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const testsRequestId = useRef(0);

  const loadAddParticipantDropdowns = async () => {
    const token = getAuthToken("token");
    const domain = handleGetLocal("pathURL");

    if (!token || !domain) {
      setAddParticipantError("Your session has expired.");
      return;
    }

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        Domain: domain,
      },
    };

    setAddParticipantError("");

    const loaded = {
      agencies: [],
      supervisors: [],
      frequencies: [],
      genders: [],
    };

    const errors = [];

    // 1. Agency (Court)
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/obligations/generic-parameter-lookup`, {
        ...config,
        params: { parameter: "Court" },
      });

      if (res.data?.isSuccess !== true) {
        throw new Error(res.data?.message || "Agency lookup failed");
      }

      loaded.agencies = (res.data?.data ?? []).map((item) => ({
        key: item.id,
        value: item.value,
      }));
    } catch (err) {
      console.error("Agency API:", err.response?.status, err);
      errors.push("Agency");
    }

    // 2. Supervisor
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/Docket/supervision-dropdown`, config);

      if (res.data?.isSuccess !== true) {
        throw new Error(res.data?.message || "Supervisor lookup failed");
      }

      loaded.supervisors = res.data?.data?.supervisor ?? [];
    } catch (err) {
      console.error("Supervisor API:", err.response?.status, err);
      errors.push("Supervisor");
    }

    // 3. Frequency
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/DrugTest/drugTestDropdownAsync`, config);

      if (res.data?.isSuccess !== true) {
        throw new Error(res.data?.message || "Frequency lookup failed");
      }

      loaded.frequencies = res.data?.data?.drugTestFrequency ?? [];
    } catch (err) {
      console.error("Frequency API:", err.response?.status, err);
      errors.push("Frequency");
    }

    // 4. Gender
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/Person/MasterDropDownValuesGet`, config);

      if (res.data?.isSuccess !== true) {
        throw new Error(res.data?.message || "Gender lookup failed");
      }

      loaded.genders = res.data?.data?.gender ?? [];
    } catch (err) {
      console.error("Gender API:", err.response?.status, err);
      errors.push("Gender");
    }

    setAddParticipantDropdowns(loaded);

    setDropdownsLoaded(errors.length === 0);

    if (errors.length > 0) {
      setAddParticipantError(`Unable to load: ${errors.join(", ")}.`);
    }
  };

  const saveSupervisionParticipant = () => {
    if (savingParticipant) return;

    const form = participantForm;

    if (!form.firstName.trim() || !form.lastName.trim() || !form.birthDate || !form.agencyId || !form.frequencyId) {
      setAddParticipantError("Please complete all required fields.");
      return;
    }

    if (form.birthDate > formatApiDate(new Date())) {
      setAddParticipantError("Birth date cannot be in the future.");
      return;
    }

    const token = getAuthToken("token");
    const domain = handleGetLocal("pathURL");

    if (!token || !domain) {
      setAddParticipantError("Your session has expired. Please sign in again.");
      return;
    }

    const payload = {
      firstName: form.firstName.trim(),
      middleName: form.middleName.trim() || null,
      lastName: form.lastName.trim(),
      altId: form.altId.trim() || null,
      birthDate: form.birthDate,
      gender: form.gender || null,
      phoneNumber: form.phoneNumber.trim() || null,
      email: form.email.trim() || null,
      agencyId: form.agencyId,
      supervisorId: form.supervisorId || null,
      frequencyId: form.frequencyId,
    };

    setSavingParticipant(true);
    setAddParticipantError("");

    axios
      .post(`${process.env.REACT_APP_API_URL}/api/Person/supervision-add-participant`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          Domain: domain,
        },
      })
      .then((res) => {
        if (res.data?.isSuccess !== true) {
          setAddParticipantError(res.data?.message || "Unable to add participant.");
          return;
        }

        closePopup();
        setParticipantForm({ ...emptyParticipantForm });
        setToastConfig({
          isVisible: true,
          type: "success",
          message: "Participant added successfully.",
        });
        setTimeout(() => setToastConfig({ isVisible: false, message: "", type: "" }), 2000);
        getSupervisionParticipants();
      })
      .catch((err) => {
        console.error("Add Participant error:", err);

        setAddParticipantError(
          err?.response?.data?.message ||
            (err?.response?.status === 401 ? "Unauthorized. Please sign in again." : "Unable to save participant."),
        );
      })
      .finally(() => {
        setSavingParticipant(false);
      });
  };

  const handleScheduleDrugTest = () => {
    if (!selected?.randomizerId || !selectedTestDateId) {
      setScheduleError("Please select a valid participant and test date.");
      return;
    }

    const token = getAuthToken("token");
    const domain = handleGetLocal("pathURL");

    if (!token || !domain) {
      setScheduleError("Your session has expired. Please sign in again.");
      return;
    }

    const payload = {
      gDrugTestRandomizerScheduleId: "00000000-0000-0000-0000-000000000000",
      gDrugTestRandomizerId: selected.randomizerId,
      gTestDateId: selectedTestDateId,
      gEntryUserId: "00000000-0000-0000-0000-000000000000",
    };

    setScheduleSaving(true);
    setScheduleError("");
    setScheduleSaveMessage("");

    axios
      .post(`${process.env.REACT_APP_API_URL}/api/RandomDrugTesting/add-edit-drug-test`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          Domain: domain,
        },
      })
      .then((response) => {
        if (response?.data?.isSuccess) {
          setScheduleSaveMessage("Drug test scheduled successfully.");
          getAvailableTestDates(selected);
          getSupervisionParticipants();
        } else {
          setScheduleError(response?.data?.message || "Unable to schedule drug test.");
        }
      })
      .catch((error) => {
        setScheduleError(
          error?.response?.status === 401 ? "Unauthorized. Please sign in again." : "Unable to schedule drug test.",
        );
        console.error("Schedule Drug Test error:", error);
      })
      .finally(() => {
        setScheduleSaving(false);
      });
  };

  const formatApiDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getScheduleDateRange = () => {
    const today = new Date();

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 30);

    return {
      dStart: formatApiDate(today),
      dEnd: formatApiDate(endDate),
    };
  };

  const getAvailableTestDates = (participant) => {
    setAvailableTestDates([]);
    setSelectedTestDateId("");
    setScheduleError("");

    if (!participant?.locationId) {
      setScheduleError("Testing location is not available for this participant.");
      return;
    }

    const token = getAuthToken("token");
    const domain = handleGetLocal("pathURL");

    if (!token || !domain) {
      setScheduleError("Your session information is missing. Please sign in again.");
      return;
    }

    const { dStart, dEnd } = getScheduleDateRange();

    setScheduleLoading(true);

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/RandomDrugTesting/get-test-date`, {
        params: {
          Location: participant.locationId,
          dStart,
          dEnd,
          includeCapacity: true,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          Domain: domain,
        },
      })
      .then((response) => {
        if (!response?.data?.isSuccess) {
          setScheduleError(response?.data?.message || "Unable to load available test dates.");
          return;
        }

        const slots = Array.isArray(response?.data?.data) ? response.data.data : [];

        const participantGender = String(participant.gender || "")
          .trim()
          .toLowerCase();

        const validSlots = slots
          .filter((slot) => {
            if (slot.bVoid === true || !slot.gTestDateId) return false;

            const capacity = Number(slot.iCapacity);
            const available = Number(slot.iAvailable);

            if (
              slot.iCapacity == null ||
              slot.iAvailable == null ||
              !Number.isFinite(capacity) ||
              !Number.isFinite(available) ||
              available <= 0
            ) {
              return false;
            }

            const slotGender = String(slot.sGender || "Any")
              .trim()
              .toLowerCase();

            const genderAllowed = slotGender === "any" || slotGender === "all" || slotGender === participantGender;

            return genderAllowed;
          })
          .sort(
            (a, b) => String(a.dTest).localeCompare(String(b.dTest)) || Number(a.iSlot || 0) - Number(b.iSlot || 0),
          );

        setAvailableTestDates(validSlots);
      })
      .catch((error) => {
        console.error("Schedule test dates API error:", error);

        if (error?.response?.status === 401) {
          setScheduleError("Unauthorized. Please verify your login session and API access.");
        } else if (error?.response?.status === 403) {
          setScheduleError("You do not have permission to view test dates.");
        } else {
          setScheduleError("Unable to load available test dates.");
        }
      })
      .finally(() => {
        setScheduleLoading(false);
      });
  };

  const getSupervisionParticipants = async () => {
    setLoading(true);
    setError("");

    const token = getAuthToken("token");
    const domain = handleGetLocal("pathURL");

    if (!token || !domain) {
      setParticipants([]);
      setError("Your session information is missing. Please sign in again.");
      setLoading(false);
      return;
    }

    await axios
      .get(`${process.env.REACT_APP_API_URL}/api/RandomDrugTesting/get-randomizer-summary`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Domain: domain,
        },
      })
      .then((val) => {
        if (val?.data?.isSuccess === true) {
          const records = Array.isArray(val?.data?.data) ? val.data.data : [];

          setParticipants(records.map((item) => mapRandomizerParticipant(item)));

          setError("");
          setPage(1);
        } else {
          setParticipants([]);
          setError(val?.data?.message || "Unable to load participants.");
        }
      })
      .catch((err) => {
        setParticipants([]);

        if (err?.response?.status === 401) {
          setError("Unauthorized. Please verify your session and API permissions.");
        } else if (err?.response?.status === 403) {
          setError("You do not have permission to view these participants.");
        } else {
          setError("Unable to load participants.");
        }

        console.error("Supervision Participants API error:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    const datePart = String(dateValue).split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [year, month, day] = datePart.split("-");
      return `${month}/${day}/${year}`;
    }

    return dateValue;
  };

  const mapRandomizerParticipant = (item) => ({
    personId: item?.gPersonId,
    randomizerId: item?.gDrugTestRandomizerId,
    locationId: item?.gEntityId,
    id: item?.sOffenderNumber ?? "",
    altId: item?.sAltId ?? "",
    firstName: item?.sFirstName ?? "",
    lastName: item?.sLastName ?? "",
    agency: item?.sLocation ?? "",

    // Format Birth Date
    dob: formatDate(item?.dDob),

    frequency: item?.sFrequencyName ?? "",
    supervisor: item?.sPrimarySupervisorName ?? "",
    gender: item?.sGenderName ?? "",

    // Format Last Test Date
    lastTestDate: formatDate(item?.dLastTest),

    // Already formatted by API, so keep unchanged
    nextTestDate: item?.sNextTestDateTime ?? "",
  });
  useEffect(() => {
    getSupervisionParticipants();
  }, []);
  const filteredData = useMemo(() => {
    const searchTerm = search.toLowerCase().trim();

    return participants
      .filter((person) => {
        const matchesSearch =
          !searchTerm ||
          columns.some((col) =>
            String(person[col.key] ?? "")
              .toLowerCase()
              .includes(searchTerm),
          );

        const matchesFilters = columns.every((col) =>
          String(person[col.key] ?? "")
            .toLowerCase()
            .includes((filters[col.key] ?? "").toLowerCase()),
        );

        return matchesSearch && matchesFilters;
      })
      .sort((a, b) => {
        const aValue = String(a[sort.key] ?? "");
        const bValue = String(b[sort.key] ?? "");
        const comparison = aValue.localeCompare(bValue, undefined, { numeric: true });

        return sort.direction === "asc" ? comparison : -comparison;
      });
  }, [participants, search, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));

  const visibleData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  const getParticipantTests = async (personId) => {
    const requestId = ++testsRequestId.current;

    setTestsLoading(true);
    setTestsError("");
    setParticipantTests([]);

    if (!personId) {
      setTestsError("Participant ID is missing.");
      setTestsLoading(false);
      return;
    }

    await axios
      .get(`${process.env.REACT_APP_API_URL}/api/DrugTest/getDrugTest`, {
        params: { personId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          Domain: handleGetLocal("pathURL"),
        },
      })
      .then((res) => {
        if (requestId !== testsRequestId.current) return;

        if (res?.data?.isSuccess === true && Array.isArray(res?.data?.data)) {
          const activeTests = res.data.data.filter((test) => test.bVoid !== true);

          setParticipantTests(activeTests);
        } else {
          setTestsError("Unable to load participant tests.");
        }
      })
      .catch((err) => {
        if (requestId !== testsRequestId.current) return;

        setTestsError(
          err?.response?.status === 401
            ? "You are not authorized to view these tests."
            : "Unable to load participant tests.",
        );

        console.error("Participant tests API error:", err);
      })
      .finally(() => {
        if (requestId === testsRequestId.current) {
          setTestsLoading(false);
        }
      });
  };

  const openPopup = (type, person) => {
    ++testsRequestId.current;

    setSelected(person);
    setPopupType(type);
    setSelectedSlot("");
    setSelectedTestDateId("");
    setMenuId(null);
    setParticipantTests([]);
    setTestsError("");
    setAvailableTestDates([]);
    setScheduleError("");

    if (type === "tests" && person?.personId) {
      getParticipantTests(person.personId);
    }

    if (type === "schedule") {
      getAvailableTestDates(person);
    }
    if (type === "add") {
      setParticipantForm({ ...emptyParticipantForm });
      setAddParticipantError("");

      if (!dropdownsLoaded) {
        loadAddParticipantDropdowns();
      }
    }
  };

  
const exportExcel = async () => {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Participants");

    // Use the same columns displayed in the Participants grid.
    worksheet.columns = columns.map((column) => ({
      header: column.label,
      key: column.key,
      width: ["agency", "supervisor", "frequency"].includes(
        column.key
      )
        ? 28
        : 19,
    }));

    // Style the header row.
    const headerRow = worksheet.getRow(1);

    headerRow.font = {
      bold: true,
      color: { argb: "FF203956" },
    };

    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEEF3F9" },
    };

    headerRow.height = 26;

    // Export all filtered and sorted participants.
    // Do not limit the export to the current page.
    filteredData.forEach((participant) => {
      const row = {};

      columns.forEach((column) => {
        let value = String(participant[column.key] ?? "");

        // Prevent spreadsheet formula injection.
        if (/^\s*[=+\-@]/.test(value)) {
          value = "'" + value;
        }

        row[column.key] = value;
      });

      worksheet.addRow(row);
    });

    // Enable Excel filtering.
    worksheet.autoFilter = {
      from: {
        row: 1,
        column: 1,
      },
      to: {
        row: 1,
        column: columns.length,
      },
    };

    // Keep column headers visible while scrolling.
    worksheet.views = [
      {
        state: "frozen",
        ySplit: 1,
      },
    ];

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "SupervisionParticipants.xlsx";

    document.body.appendChild(link);
    link.click();
    link.remove();

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (error) {
    console.error("Participant Excel export error:", error);
    setError("Unable to export participants to Excel.");
  }
};


  const exportCSV = () => {
    const header = columns.map((c) => csvCell(c.label)).join(",");
    const rows = filteredData.map((p) => columns.map((c) => csvCell(p[c.key])).join(","));

    downloadFile("\uFEFF" + [header, ...rows].join("\r\n"), "SupervisionParticipants.csv", "text/csv;charset=utf-8");
  };

  const closePopup = () => {
    ++testsRequestId.current;
    setPopupType(null);
    setSelected(null);
    setParticipantTests([]);
  };

  const detailFields = selected
    ? [
        ["Participant Id", selected.id],
        ["Alt Id", selected.altId],
        ["Last Name", selected.lastName],
        ["First Name", selected.firstName],
        ["Agency", selected.agency],
        ["Birth Date", selected.dob],
        ["Frequency", selected.frequency],
        ["Supervisor", selected.supervisor],
        ["Gender", selected.gender],
        ["Last Test Date", selected.lastTestDate],
        ["Next Test Date", selected.nextTestDate],
        ["Enrollment Status", selected.status],
        ["Phone", selected.phone ?? "—"],
        ["Email", selected.email ?? "—"],
      ]
    : [];

  const todayKey = [
    new Date().getFullYear(),
    String(new Date().getMonth() + 1).padStart(2, "0"),
    String(new Date().getDate()).padStart(2, "0"),
  ].join("-");

  const testRows = participantTests
    .filter((test) => test.bVoid !== true)
    .map((test) => {
      const dateKey = getTestDateKey(test.dTest);

      const isMissed = test.bMissedTest === true || /^missed\b/i.test((test.sDrugTestResultName || "").trim());

      const hasResult = !!test.gDrugTestResultId || !!test.sDrugTestResultName;

      let status = "Recorded";

      if (isMissed) {
        status = "Missed";
      } else if (dateKey > todayKey) {
        status = "Future dated";
      } else if (hasResult) {
        status = "Completed";
      }

      return {
        id: test.gDrugTestId,
        dateKey,
        testDate: formatTestDate(test.dTest),
        status,
        testType: test.sDrugTestTypeName || "—",
        resultDate: formatTestDate(test.dResult),
        result: isMissed ? test.sDrugTestResultName || "Missed" : test.sDrugTestResultName || "—",
      };
    });

  const scheduledDateKey = getTestDateKey(selected?.nextTestDate);

  // Add the next scheduled test if it is not already in the records.
  if (scheduledDateKey && scheduledDateKey >= todayKey && !testRows.some((row) => row.dateKey === scheduledDateKey)) {
    testRows.push({
      id: "next-scheduled",
      dateKey: scheduledDateKey,
      testDate: formatTestDate(selected.nextTestDate),
      status: "Scheduled",
      testType: "—",
      resultDate: "—",
      result: "—",
    });
  }

  testRows.sort((a, b) => b.dateKey.localeCompare(a.dateKey));

  const completedCount = testRows.filter((row) => row.status === "Completed").length;

  const scheduledCount = testRows.filter((row) => row.status === "Scheduled").length;

  return (
    <div className="sp-participants">
      <Toster message={toastConfig.message} type={toastConfig.type} visible={toastConfig.isVisible} />
      <div className="sp-page-heading">
        <h2>Drug Test Participants</h2>
        <p>Records available under your Pronto permissions.</p>
      </div>

      <div className="sp-card">
        <div className="sp-toolbar">
          <div className="sp-toolbar-section">
            <span className="sp-toolbar-title">Participants</span>
            <button className="sp-btn" onClick={() => openPopup("add", null)}>
              <Plus size={15} /> Add Participant
            </button>
          </div>

          <div className="sp-toolbar-section">
            <button className="sp-btn" onClick={exportCSV}>
              <Download size={15} /> Export to CSV
            </button>
            <button
  type="button"
  className="sp-btn"
  onClick={exportExcel}
  disabled={loading}
>
  <Download size={15} />
  Export to Excel
</button>
          </div>

          <div className="sp-search">
            <Search size={17} />
            <input
              placeholder="Search participants..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        <div className="sp-table-scroll">
          <table className="sp-table">
            <thead>
              <tr>
                <th>Actions</th>
                {columns.map((col) => (
                  <th key={col.key} onClick={() => handleSort(col.key)} className="sp-sortable">
                    {col.label}
                    <span>{sort.key === col.key ? (sort.direction === "asc" ? " ↑" : " ↓") : " ↕"}</span>
                  </th>
                ))}
              </tr>

              <tr className="sp-filter-row">
                <th />
                {columns.map((col) => (
                  <th key={col.key}>
                    <input
                      placeholder="Filter..."
                      value={filters[col.key] ?? ""}
                      onChange={(e) => {
                        setFilters((prev) => ({
                          ...prev,
                          [col.key]: e.target.value,
                        }));
                        setPage(1);
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {visibleData.map((person) => (
                <tr key={person.id}>
                  <td>
                    <div className="sp-actions">
                      <button
                        className="sp-btn sp-icon-btn"
                        onClick={() => setMenuId(menuId === person.id ? null : person.id)}
                      >
                        <MoreHorizontal size={17} />
                      </button>

                      {menuId === person.id && (
                        <div className="sp-action-menu">
                          <button onClick={() => openPopup("detail", person)}>View Detail</button>
                          <button onClick={() => openPopup("tests", person)}>View Tests</button>
                          <button onClick={() => openPopup("schedule", person)}>Schedule Drug Test</button>
                        </div>
                      )}
                    </div>
                  </td>

                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.key === "id" ? (
                        <button className="sp-id-link" onClick={() => openPopup("detail", person)}>
                          {person.id}
                        </button>
                      ) : (
                        (person[col.key] ?? "—")
                      )}
                    </td>
                  ))}
                </tr>
              ))}

              {visibleData.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="sp-empty">
                    No participants found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="sp-footer">
          {/* Page size options - left side */}
          <div className="sp-page-sizes">
            {[10, 20, 30, 50, 100].map((size) => (
              <button
                key={size}
                type="button"
                className={`sp-page-size-btn ${pageSize === size ? "active" : ""}`}
                onClick={() => {
                  setPageSize(size);
                  setPage(1);
                }}
              >
                {size}
              </button>
            ))}
          </div>

          {/* Pagination - right side */}
          <div className="sp-pagination-right">
            <span className="sp-page-summary">
              Page {page} of {totalPages} ({filteredData.length} items)
            </span>

            <div className="sp-page-controls">
              <button
                type="button"
                className="sp-page-arrow"
                disabled={page === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                aria-label="Previous page"
              >
                ‹
              </button>

              {Array.from({ length: totalPages }, (_, index) => index + 1)
                .filter(
                  (pageNumber) => pageNumber === 1 || pageNumber === totalPages || Math.abs(pageNumber - page) <= 1,
                )
                .map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={`sp-page-number ${page === pageNumber ? "active" : ""}`}
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}

              <button
                type="button"
                className="sp-page-arrow"
                disabled={page === totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      <Popup
        visible={popupType !== null}
        onHiding={closePopup}
        showTitle={false}
        showCloseButton={false}
        hideOnOutsideClick={true}
        dragEnabled={false}
        width="min(680px, 95vw)"
        height={
  popupType === "add"
    ? 720
    : popupType === "detail"
      ? 650
      : 560
}
maxHeight="92vh"
        onShown={(e) => {
          const content = e.component.content();
          if (content) content.scrollTop = 0;
        }}
      >
        <div className="sp-popup">
          <div className="sp-popup-header">
            <div>
              <h3>
                {popupType === "detail"
                  ? "Participant Details"
                  : popupType === "tests"
                    ? "Participant Tests"
                    : popupType === "schedule"
                      ? "Schedule Drug Test"
                      : "Add Participant"}
              </h3>
              {selected && (
                <p>
                  {formatName(selected)} · {selected.id}
                </p>
              )}
            </div>

            <button className="sp-btn" onClick={closePopup}>
              Close
            </button>
          </div>

          {popupType === "detail" && (
            <div className="sp-detail-grid">
              {detailFields.map(([label, value]) => (
                <div key={label}>
                  <label>{label}</label>
                  <p>{value}</p>
                </div>
              ))}
            </div>
          )}

          {popupType === "tests" && (
            <div className="sp-preview sp-tests-preview">
              {testsLoading ? (
                <p>Loading participant tests...</p>
              ) : testsError ? (
                <div className="sp-tests-error">
                  <p>{testsError}</p>
                  <button type="button" className="sp-btn" onClick={() => getParticipantTests(selected?.personId)}>
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <p className="sp-tests-summary">
                    {completedCount} completed · {scheduledCount} scheduled
                  </p>

                  <div className="sp-tests-table-scroll">
                    <table className="sp-popup-table">
                      <thead>
                        <tr>
                          <th>Test Date</th>
                          <th>Status</th>
                          <th>Test Type</th>
                          <th>Result Date</th>
                          <th>Test Result</th>
                        </tr>
                      </thead>

                      <tbody>
                        {testRows.length > 0 ? (
                          testRows.map((test) => (
                            <tr key={test.id}>
                              <td>{test.testDate}</td>
                              <td>{test.status}</td>
                              <td>{test.testType}</td>
                              <td>{test.resultDate}</td>
                              <td>{test.result}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="sp-empty">
                              No drug tests found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {popupType === "schedule" && (
            <div className="sp-preview sp-schedule-preview">
              <label htmlFor="supervision-test-date">Test Date / Time</label>

              {scheduleLoading ? (
                <p>Loading available test dates...</p>
              ) : scheduleError ? (
                <div>
                  <p className="sp-tests-error">{scheduleError}</p>

                  <button type="button" className="sp-btn" onClick={() => getAvailableTestDates(selected)}>
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <select
                    id="supervision-test-date"
                    value={selectedTestDateId}
                    onChange={(e) => setSelectedTestDateId(e.target.value)}
                    disabled={availableTestDates.length === 0}
                  >
                    <option value="">
                      {availableTestDates.length === 0
                        ? "No available test dates"
                        : "Select a valid test date and time"}
                    </option>

                    {availableTestDates.map((slot) => {
                      const dateKey = getTestDateKey(slot.dTest);

                      const dayName = dateKey
                        ? new Date(`${dateKey}T12:00:00`).toLocaleDateString("en-US", { weekday: "long" })
                        : "";

                      const label =
                        `${dayName}, ${slot.sTestDateTime || slot.sTestDate} | ` +
                        `Gender: ${slot.sGender || "Any"} | ` +
                        `${slot.iAvailable} available / ` +
                        `${slot.iCapacity} capacity`;

                      return (
                        <option key={slot.gTestDateId} value={slot.gTestDateId}>
                          {label}
                        </option>
                      );
                    })}
                  </select>

                  {availableTestDates.length === 0 && (
                    <p>No available test dates found for this participant within the next 30 days.</p>
                  )}

                  {selectedTestDateId && <p className="sp-schedule-selection">Test date and time selected.</p>}

                  {/* <p>
                    Scheduling will be enabled once the existing save API and participant eligibility checks are
                    connected.
                  </p> */}
                  {scheduleError && <p className="sp-tests-error">{scheduleError}</p>}

                  {scheduleSaveMessage && <p className="sp-schedule-selection">{scheduleSaveMessage}</p>}

                  <button
                    type="button"
                    className="sp-btn"
                    onClick={handleScheduleDrugTest}
                    disabled={!selectedTestDateId || scheduleSaving || scheduleLoading}
                  >
                    {scheduleSaving ? "Scheduling..." : "Schedule Test"}
                  </button>
                </>
              )}
            </div>
          )}

          {popupType === "add" && (
            <div className="sp-add-participant-form">
              <p className="sp-add-description">Enter participant information</p>

              <div className="sp-add-form-grid">
                {/* Text fields */}
                {[
                  ["First Name", "firstName", "text", true],
                  ["Middle Name", "middleName", "text", false],
                  ["Last Name", "lastName", "text", true],
                  ["Phone Number", "phoneNumber", "tel", false],
                  ["Email", "email", "email", false],
                  ["Alt Id", "altId", "text", false],
                ].map(([label, name, type, required]) => (
                  <div className="sp-add-form-field" key={name}>
                    <label htmlFor={`sp-${name}`}>
                      {label}
                      {required ? " *" : ""}
                    </label>

                    <input
                      id={`sp-${name}`}
                      name={name}
                      type={type}
                      value={participantForm[name]}
                      onChange={handleParticipantFieldChange}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      required={required}
                    />
                  </div>
                ))}

                {/* Birth Date - DevExtreme Calendar */}
                <div className="sp-add-form-field">
                  <label htmlFor="sp-birthDate">Birth Date *</label>

                  <DateBox
                    id="sp-birthDate"
                    type="date"
                    pickerType="calendar"
                    displayFormat="MM/dd/yyyy"
                    placeholder="MM/DD/YYYY"
                    value={participantForm.birthDate ? new Date(`${participantForm.birthDate}T12:00:00`) : null}
                    max={new Date()}
                    openOnFieldClick={true}
                    showDropDownButton={true}
                    useMaskBehavior={true}
                    onValueChanged={(e) => {
                      setParticipantForm((prev) => ({
                        ...prev,
                        birthDate: e.value ? formatApiDate(new Date(e.value)) : "",
                      }));
                    }}
                  />
                </div>

                {/* Agency */}
                <div className="sp-add-form-field">
                  <label htmlFor="sp-agency">Agency *</label>

                  <select
                    id="sp-agency"
                    name="agencyId"
                    value={participantForm.agencyId}
                    onChange={handleParticipantFieldChange}
                    required
                  >
                    <option value="">Select agency</option>

                    {addParticipantDropdowns.agencies.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.value}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Supervisor */}
                <div className="sp-add-form-field">
                  <label htmlFor="sp-supervisor">Supervisor</label>

                  <select
                    id="sp-supervisor"
                    name="supervisorId"
                    value={participantForm.supervisorId}
                    onChange={handleParticipantFieldChange}
                  >
                    <option value="">Select supervisor</option>

                    {addParticipantDropdowns.supervisors.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.value}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Frequency */}
                <div className="sp-add-form-field">
                  <label htmlFor="sp-frequency">Frequency *</label>

                  <select
                    id="sp-frequency"
                    name="frequencyId"
                    value={participantForm.frequencyId}
                    onChange={handleParticipantFieldChange}
                    required
                  >
                    <option value="">Select frequency</option>

                    {addParticipantDropdowns.frequencies.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.value}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Gender */}
                <div className="sp-add-form-field">
                  <label htmlFor="sp-gender">Gender</label>

                  <select
                    id="sp-gender"
                    name="gender"
                    value={participantForm.gender}
                    onChange={handleParticipantFieldChange}
                  >
                    <option value="">Select gender</option>

                    {addParticipantDropdowns.genders.map((item) => {
                      const value = item.value ?? item.sName ?? "";

                      return (
                        <option key={item.key ?? item.gGenderId ?? value} value={value}>
                          {value}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Error message */}
              {addParticipantError && (
                <p className="sp-tests-error" role="alert">
                  {addParticipantError}
                </p>
              )}

              {/* Save button */}
              <div className="sp-add-form-actions">
                <button
                  type="button"
                  className="sp-btn"
                  onClick={saveSupervisionParticipant}
                  disabled={
                    savingParticipant ||
                    addParticipantDropdowns.agencies.length === 0 ||
                    addParticipantDropdowns.frequencies.length === 0
                  }
                >
                  {savingParticipant ? "Saving..." : "Add Participant"}
                </button>
              </div>
            </div>
          )}

          <div className="sp-popup-footer">
            <button className="sp-btn" onClick={closePopup}>
              Close Preview
            </button>
          </div>
        </div>
      </Popup>
    </div>
  );
}
